import React, { useEffect, useState } from 'react';
import api from '../services/api';

const ACTION_LABELS = {
  create: { icon: '🟢', label: 'Creado' },
  update: { icon: '🔵', label: 'Actualizado' },
  delete: { icon: '🔴', label: 'Eliminado' },
};

function formatDate(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleString('es-ES', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

function diffFields(oldVal, newVal) {
  if (!oldVal || !newVal) return null;
  const keys = new Set([...Object.keys(oldVal), ...Object.keys(newVal)]);
  const changes = [];
  for (const k of keys) {
    if (k === 'updated_at' || k === 'created_at') continue;
    const a = JSON.stringify(oldVal[k] ?? null);
    const b = JSON.stringify(newVal[k] ?? null);
    if (a !== b) changes.push({ field: k, from: oldVal[k], to: newVal[k] });
  }
  return changes.length ? changes : null;
}

export default function ServerChangelog({ serverId, companyId }) {
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({});

  useEffect(() => {
    if (!serverId || !companyId) return;
    setLoading(true);
    api.getServerChangelog(serverId, companyId, 100)
      .then(r => { setLogs(r.data || []); setTotal(r.total || 0); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [serverId, companyId]);

  if (loading) return <div className="text-center p-3"><div className="spinner-border spinner-border-sm" /></div>;

  if (!logs.length) {
    return <div className="text-muted text-center p-4">Sin actividad registrada</div>;
  }

  return (
    <div className="server-changelog">
      <small className="text-muted d-block mb-2">{total} entradas</small>
      <div className="list-group list-group-flush" style={{ maxHeight: 400, overflowY: 'auto' }}>
        {logs.map((log) => {
          const meta = ACTION_LABELS[log.action] || { icon: '⚪', label: log.action };
          const changes = diffFields(log.old_value, log.new_value);
          const isExpanded = !!expanded[log.id];

          return (
            <div key={log.id} className="list-group-item px-2 py-2"
              style={{ cursor: changes ? 'pointer' : 'default', fontSize: '0.85rem' }}
              onClick={() => changes && setExpanded((p) => ({ ...p, [log.id]: !p[log.id] }))}
            >
              <div className="d-flex justify-content-between align-items-center">
                <span>
                  <span className="me-1">{meta.icon}</span>
                  <strong>{meta.label}</strong>
                  {log.description && <span className="text-muted ms-2">— {log.description}</span>}
                </span>
                <small className="text-muted text-nowrap ms-2">
                  {formatDate(log.timestamp)}
                  {log.user_email && <span className="ms-1">({log.user_email})</span>}
                </small>
              </div>
              {isExpanded && changes && (
                <table className="table table-sm table-borderless mt-2 mb-0" style={{ fontSize: '0.8rem' }}>
                  <thead><tr><th>Campo</th><th>Antes</th><th>Después</th></tr></thead>
                  <tbody>
                    {changes.map((c, i) => (
                      <tr key={i}>
                        <td className="fw-semibold">{c.field}</td>
                        <td className="text-danger">{typeof c.from === 'object' ? JSON.stringify(c.from) : String(c.from ?? '—')}</td>
                        <td className="text-success">{typeof c.to === 'object' ? JSON.stringify(c.to) : String(c.to ?? '—')}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
